import {
  CheckSquare,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  User,
} from "lucide-react";
import React from "react";

import { TaskQuickCreateRow } from "./TaskCreateModal";
import { CommentCell, InlineEstimateCell, StatusCell, TimeCell } from "./TaskCells";
import {
  DESKTOP_GRID_CLASS,
  getBillingMeta,
  getStatusMeta,
} from "./taskConstants";
import { formatHours, getCommentPreview } from "./taskUtils";

export const TaskDesktopRow = ({
  task,
  client,
  showClient,
  activeCell,
  setActiveCell,
  pendingKeys,
  onOpenDetails,
  onPatchTask,
  onAddTimeEntry,
}) => {
  const billingMeta = getBillingMeta(task.statut_facturation);

  return (
    <div
      className={`hidden items-center gap-4 px-4 py-3 transition hover:bg-slate-50 lg:grid ${DESKTOP_GRID_CLASS}`}
    >
      <div className="min-w-0">
        <button
          type="button"
          onClick={() => {
            setActiveCell(null);
            onOpenDetails(task.id);
          }}
          className="block w-full truncate text-left text-[15px] font-semibold text-slate-900 transition hover:text-primary-700"
        >
          {task.titre}
        </button>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          {showClient && client && (
            <span className="inline-flex items-center gap-1">
              <User className="h-3.5 w-3.5" />
              {client.nom}
            </span>
          )}
          <span
            className={`inline-flex rounded-full px-2 py-0.5 font-medium ${billingMeta.className}`}
          >
            {billingMeta.label}
          </span>
          <span>Reste {formatHours(task.heures_a_facturer)}</span>
          {task.notes_internes ? <span>Note interne</span> : null}
        </div>
      </div>

      <InlineEstimateCell
        task={task}
        activeCell={activeCell}
        setActiveCell={setActiveCell}
        pending={Boolean(pendingKeys[`${task.id}:estimate`])}
        onSave={onPatchTask}
      />

      <TimeCell
        task={task}
        activeCell={activeCell}
        setActiveCell={setActiveCell}
        pending={Boolean(pendingKeys[`${task.id}:temps`])}
        onSave={onAddTimeEntry}
      />

      <StatusCell
        task={task}
        activeCell={activeCell}
        setActiveCell={setActiveCell}
        pending={Boolean(pendingKeys[`${task.id}:status`])}
        onSave={onPatchTask}
      />

      <CommentCell
        task={task}
        activeCell={activeCell}
        setActiveCell={setActiveCell}
        pending={Boolean(pendingKeys[`${task.id}:commentaire`])}
        onSave={onPatchTask}
      />
    </div>
  );
};

export const TaskMobileCard = ({
  task,
  client,
  showClient,
  activeCell,
  setActiveCell,
  pendingKeys,
  onOpenDetails,
  onPatchTask,
  onAddTimeEntry,
}) => {
  const billingMeta = getBillingMeta(task.statut_facturation);

  return (
    <div className="space-y-3 border-b border-slate-200 px-4 py-4 lg:hidden">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => {
              setActiveCell(null);
              onOpenDetails(task.id);
            }}
            className="block text-left text-sm font-semibold text-slate-900"
          >
            {task.titre}
          </button>
          <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
            {showClient && client ? <span>{client.nom}</span> : null}
            <span className={`rounded-full px-2 py-0.5 ${billingMeta.className}`}>
              {billingMeta.label}
            </span>
            <span>
              {task.heures_estimees == null
                ? "Sans estimation"
                : formatHours(task.heures_estimees)}
            </span>
          </div>
        </div>

        <div className="w-36 shrink-0">
          <StatusCell
            task={task}
            activeCell={activeCell}
            setActiveCell={setActiveCell}
            pending={Boolean(pendingKeys[`${task.id}:status`])}
            onSave={onPatchTask}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
            Temps suivi
          </p>
          <TimeCell
            task={task}
            activeCell={activeCell}
            setActiveCell={setActiveCell}
            pending={Boolean(pendingKeys[`${task.id}:temps`])}
            onSave={onAddTimeEntry}
          />
        </div>

        <button
          type="button"
          onClick={() => {
            setActiveCell(null);
            onOpenDetails(task.id);
          }}
          className="rounded-xl border border-slate-200 px-3 py-2 text-left text-sm text-slate-600 transition hover:bg-slate-50"
        >
          <span className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
            <MessageSquare className="h-3.5 w-3.5" />
            Détail
          </span>
          <span className="line-clamp-2 block">
            {task.commentaire ? getCommentPreview(task.commentaire) : "Ouvrir la fiche"}
          </span>
        </button>
      </div>
    </div>
  );
};

export const TaskStatusSection = ({
  status,
  tasks,
  collapsed,
  clientMap,
  showClient,
  activeCell,
  setActiveCell,
  pendingKeys,
  onToggle,
  onOpenDetails,
  onPatchTask,
  onAddTimeEntry,
  quickCreate,
  clients,
  filteredClientId,
  quickCreating,
  onActivateQuickCreate,
  onCancelQuickCreate,
  onQuickCreateChange,
  onQuickCreateSubmit,
}) => {
  const statusMeta = getStatusMeta(status);
  const isQuickCreateActive = quickCreate?.status === status;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => onToggle(status)}
        className="flex w-full items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 text-left"
      >
        <div className="flex min-w-0 items-center gap-3">
          {collapsed ? (
            <ChevronRight className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          )}
          <span
            className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${statusMeta.headerClass}`}
          >
            {statusMeta.label}
          </span>
          <span className="text-sm font-medium text-slate-500">{tasks.length}</span>
        </div>

        <span className="hidden text-xs uppercase tracking-[0.18em] text-slate-400 lg:block">
          Vue liste
        </span>
      </button>

      {!collapsed && (
        <>
          <div
            className={`hidden items-center gap-4 border-b border-slate-200 bg-slate-50/80 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 lg:grid ${DESKTOP_GRID_CLASS}`}
          >
            <div>Nom</div>
            <div>Estimation</div>
            <div>Temps suivi</div>
            <div>Statut</div>
            <div>Commentaires</div>
          </div>

          {tasks.length === 0 ? (
            <div className="px-4 py-6 text-sm text-slate-500">
              Aucune tâche dans cette section.
            </div>
          ) : (
            <div>
              {tasks.map((task) => (
                <React.Fragment key={task.id}>
                  <TaskDesktopRow
                    task={task}
                    client={clientMap[task.client_id]}
                    showClient={showClient}
                    activeCell={activeCell}
                    setActiveCell={setActiveCell}
                    pendingKeys={pendingKeys}
                    onOpenDetails={onOpenDetails}
                    onPatchTask={onPatchTask}
                    onAddTimeEntry={onAddTimeEntry}
                  />
                  <TaskMobileCard
                    task={task}
                    client={clientMap[task.client_id]}
                    showClient={showClient}
                    activeCell={activeCell}
                    setActiveCell={setActiveCell}
                    pendingKeys={pendingKeys}
                    onOpenDetails={onOpenDetails}
                    onPatchTask={onPatchTask}
                    onAddTimeEntry={onAddTimeEntry}
                  />
                </React.Fragment>
              ))}
            </div>
          )}

          <TaskQuickCreateRow
            status={status}
            active={isQuickCreateActive}
            quickCreate={quickCreate || { titre: "", client_id: filteredClientId || "" }}
            clients={clients}
            filteredClientId={filteredClientId}
            creating={quickCreating}
            onActivate={onActivateQuickCreate}
            onCancel={onCancelQuickCreate}
            onChange={onQuickCreateChange}
            onSubmit={onQuickCreateSubmit}
          />
        </>
      )}
    </section>
  );
};

export const EmptyTaskState = () => (
  <div className="card py-16 text-center">
    <CheckSquare className="mx-auto mb-4 h-12 w-12 text-slate-300" />
    <h3 className="text-lg font-medium text-slate-900">Aucune tâche</h3>
    <p className="mt-2 text-sm text-slate-600">
      Commencez par créer une tâche ou utilisez l’ajout rapide par statut.
    </p>
  </div>
);
